/// <amd-module name="Search/Code/Components/FilterPane" />

import * as React from "react";
import * as Constants from "Search/Code/Constants";
import * as Container from "Search/Shared/Base/Container";
import * as SearchPickList from "SearchUI/SearchPickList";
import * as SearchShared from "Search/LegacyWebApi/Contracts/Search.SharedLegacy";
import * as Resources from "Search/Code/Resources";
import * as SharedResources from "Search/Shared/Resources";
import { BranchSelectorFilterBarItem } from "Search/Code/Components/BranchSelectorFilterBarItem";
import { format } from "VSS/Core/Util/String";
import { compare } from "Search/Shared/Utils";
import { FilterBar } from "SearchUI/FilterBar"
import { IFilterStoreState, FilterType } from "Search/Shared/Base/Stores/FilterStore";
import { ISearchStoreState } from "Search/Shared/Base/Stores/SearchStore";
import { SearchQuery, CodeQueryResponse } from "Search/LegacyWebApi/Contracts/Code";
import { TreeFilterBarItem } from "Search/Code/Components/TreeFilterBarItem";
import { ActionCreator } from "Search/Code/Flux/ActionCreator";
import { StoresHub, IAggregatedState, StoreName } from "Search/Code/Flux/StoresHub"
import { ContentLoadState } from "SearchUI/Components/PathSelector/PathSelector.Props";

const searchPickListMenuMaxWidth: number = 510;

export interface IFilterPaneProps extends Container.IContainerProps<StoreName, ActionCreator, IAggregatedState, StoresHub> {
    searchStoreState: ISearchStoreState<SearchQuery, CodeQueryResponse>;

    filterStoreState: IFilterStoreState;
    
    isProjectContext: boolean;
}

export const FilterPane = (props: IFilterPaneProps) => {
    const { query } = props.searchStoreState;
    const { onFiltersChanged, onFilterClick } = props.actionCreator;
    const { filter, filterItems, filterCategories } = props.filterStoreState;
    return (
        <FilterBar
            filter={filter}
            isProjectContext={props.isProjectContext}>
            {
                Object
                    .keys(filterItems)
                    .map((key: string) => {
                        const filterType = filterItems[key].filterType;
                        const visible = filterItems[key].visible;
                        const enabled = filterItems[key].enabled;

                        if (filterType === FilterType.PickList && visible) {
                            let items: SearchShared.Filter[] = [];
                            if (enabled) {
                                const filters = filterCategories
                                    .filter(c => c.name === key)[0];
                                items = filters ? filters.filters : [];
                            }

                            return <SearchPickList.SearchPickListFilterBarItem
                                key={key}
                                filterItemKey={key}
                                disableAnnounce={true}
                                menuMaxWidth={searchPickListMenuMaxWidth}
                                isExpandable={true}
                                lightWeightDisplay={true}
                                dataLoading={ props.filterStoreState.filtersLoading }
                                {...searchPickListPropCreators[key](cloneItems(items), filterItems[key].enabled, onFiltersChanged, onFilterClick)}
                                />;
                        } else if (filterType === FilterType.Branch && visible) {
                            let items: SearchShared.Filter[] = [];
                            let project: string | undefined;
                            let repoName: string | undefined;
                            if (enabled) {
                                const filters = filterCategories
                                    .filter(c => c.name === key)[0];
                                const { searchFilters } = query;

                                items = filters ? filters.filters : [];
                                project = searchFilters[Constants.FilterKeys.ProjectFiltersKey][0];
                                repoName = searchFilters[Constants.FilterKeys.RepositoryFiltersKey][0];
                            }

                            return <BranchSelectorFilterBarItem
                                key={key}
                                filterItemKey={key}
                                items={cloneItems(items)}
                                enabled={filterItems[key].enabled}
                                project={project}
                                repositoryName={repoName}
                                onSelectionChanged={onFiltersChanged}
                                showFooter={props.isMember}
                                onClick={onFilterClick}                                
                                contentLoadState={ props.filterStoreState.filtersLoading ? ContentLoadState.Loading : ContentLoadState.LoadSuccess}
                             />;
                        } else if (filterType === FilterType.Path && visible) {
                            let project: string | undefined;
                            let repository: string | undefined;
                            let branch: string | undefined;
                            let requestedPath: string | undefined;
                            if (enabled) {
                                const { searchFilters } = query;
                                project = searchFilters[Constants.FilterKeys.ProjectFiltersKey][0];
                                repository = searchFilters[Constants.FilterKeys.RepositoryFiltersKey][0];
                                branch = searchFilters[Constants.FilterKeys.BranchFiltersKey] &&
                                    searchFilters[Constants.FilterKeys.BranchFiltersKey][0];
                                requestedPath = searchFilters[Constants.FilterKeys.PathFiltersKey] &&
                                    searchFilters[Constants.FilterKeys.PathFiltersKey][0];
                            }

                            return <TreeFilterBarItem
                                key={key}
                                filterItemKey={key}
                                project={project}
                                repository={repository}
                                branch={branch}
                                requestedPath={requestedPath}
                                enabled={filterItems[key].enabled}
                                onSelectionChanged={onFiltersChanged}
                            />;
                        }
                    })
            }
        </FilterBar>);
};

function cloneItems(filterItems: SearchShared.Filter[]): SearchShared.Filter[] {
    const clonedItems: SearchShared.Filter[] = [];
    filterItems.forEach(item => clonedItems.push(Object.assign({}, item)));

    return clonedItems;
}

function getFooterMessage(
    filterCount: number,
    text: string,
    showingSingleItemText: string,
    showingMultipleItemsText: string,
    foundSingleItemText: string,
    foundMultipleItemsText: string): string {
    const isSearchTextPresent = !!text && text !== "";
    filterCount = !isSearchTextPresent ? filterCount - 1 : filterCount; // Adjust for "All" item.

    return !isSearchTextPresent
        ? (filterCount > 1 ? format(showingMultipleItemsText, filterCount.toString()) : showingSingleItemText)
        : (filterCount > 1
            ? format(foundMultipleItemsText, filterCount.toString())
            : filterCount === 0
                ? format(SharedResources.NoResultsForSearchText, text)
                : foundSingleItemText);
}

function filterItems(items: SearchPickList.ISearchPickListItem[], searchText: string): SearchPickList.ISearchPickListItem[] {
    searchText = searchText.toLowerCase();
    return items
        .filter((item) => item.name.toLowerCase().indexOf(searchText) >= 0)
        .sort((a, b) => compare(a.name, b.name, searchText));
}

const searchPickListPropCreators = {
    [Constants.FilterKeys.ProjectFiltersKey]:
        (items: SearchPickList.ISearchPickListItem[],
            enabled: boolean, 
            onSelectionChanged: (name: string) => void,
            onClick: () => void): SearchPickList.ISearchPickListProps => ({
            name: Constants.FilterKeys.ProjectFiltersKey,
            items: items,
            allItemDisplayName: SharedResources.AllProjectsDisplayLabel,
            allItemLabel: SharedResources.All,
            width: 160,
            enabled: enabled,
            searchTextPlaceholder: SharedResources.FindProjectPlaceholder,
            displayName: SharedResources.ProjectsDisplayLabel,
            onGetFooterMessage: (itemCount: number, searchText: string) =>
                getFooterMessage(
                    itemCount,
                    searchText,
                    SharedResources.ShowingSingleProject,
                    SharedResources.ShowingMultipleProjects,
                    SharedResources.FoundSingleProject,
                    SharedResources.FoundMultipleProjects),
            onRefineItems: filterItems,
            onSelectionChanged,
            calloutProps: {
                title: SharedResources.RefineSearchTermText,
                content: SharedResources.ProjectFilterCalloutContent
            },
            onClick
        }),
    [Constants.FilterKeys.RepositoryFiltersKey]:
        (items: SearchPickList.ISearchPickListItem[],
            enabled: boolean, 
            onSelectionChanged: (name: string) => void,
            onClick: () => void): SearchPickList.ISearchPickListProps => ({
            name: Constants.FilterKeys.RepositoryFiltersKey,
            items: items,
            allItemDisplayName: Resources.AllRepositoriesDisplayLabel,
            allItemLabel: SharedResources.All,
            width: 160,
            enabled: enabled,
            searchTextPlaceholder: Resources.FindRepoPlaceholder,
            displayName: Resources.RepoDisplayLabel,
            onGetFooterMessage: (itemCount: number, searchText: string) =>
                getFooterMessage(
                    itemCount,
                    searchText,
                    Resources.ShowingSingleRepo,
                    Resources.ShowingMultipleRepos,
                    Resources.FoundSingleRepo,
                    Resources.FoundMultipleRepos),
            onRefineItems: filterItems,
            onSelectionChanged,
            calloutProps: {
                title: SharedResources.RefineFiltersText,
                content: Resources.RepositoryFilterCalloutContent
            },
            onClick
        }),
    [Constants.FilterKeys.CodeTypeFiltersKey]:
        (items: SearchPickList.ISearchPickListItem[],
            enabled: boolean,
            onSelectionChanged: (name: string) => void,
            onClick: () => void): SearchPickList.ISearchPickListProps => ({
            name: Constants.FilterKeys.CodeTypeFiltersKey,
            items: items,
            allItemDisplayName: Resources.AllCodeTypesDisplayLabel,
            allItemLabel: SharedResources.All,
            width: 160,
            enabled: enabled,
            searchTextPlaceholder: Resources.FindCodeTypePlaceholder,
            displayName: Resources.CodeTypeDisplayLabel,
            onGetFooterMessage: (itemCount: number, searchText: string) =>
                getFooterMessage(
                    itemCount,
                    searchText,
                    Resources.ShowingSingleCodeType,
                    Resources.ShowingMultipleCodeTypes,
                    Resources.FoundSingleCodeType,
                    Resources.FoundMultipleCodeTypes),
            onRefineItems: filterItems,
            onSelectionChanged,
            calloutProps: {
                title: SharedResources.RefineSearchTermText,
                content: Resources.CodeTypeFilterCalloutContent
            },
            compactMode: true,
            compactModeCalloutProps: {
                title: SharedResources.RefineSearchTermText,
                content: items.length > 0
                    ? format(
                        items[0].resultCount === 1
                            ? Resources.CompactModeCalloutContentWithSingleMatch
                            : Resources.CompactModeCalloutContent,
                        items[0].resultCount.toString(),
                        items[0].name.toLowerCase())
                    : ""
            }
        })
};